import type { CasePrepUscisFieldProvenance } from "../../types";
import type { ResolveFieldSourceUser } from "./types";

export type AttributionAvatar =
  | { kind: "person"; name: string; initials: string; imageUrl: string | null }
  | { kind: "agent" };

/**
 * Who or what put a value in a field, and when: the card's `MetaLine`.
 * `avatar` is null when nobody can be pictured (filing rules, no recorded
 * actor), so there is never a "?" placeholder.
 */
export type FieldSourceAttribution = {
  /** Joined with " · " by `MetaLine`. */
  parts: string[];
  avatar: AttributionAvatar | null;
};

type TimestampLike = {
  toDate?: () => Date;
  seconds?: number;
};

/** A Firestore timestamp, its JSON forms, an ISO or HTTP date string, or epoch ms. */
export function parseTimestamp(value: unknown): Date | null {
  if (value === null || value === undefined || value === "") return null;
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "object") {
    const stamp = value as TimestampLike;
    const jsonSeconds: unknown = Reflect.get(stamp, "_seconds");
    if (typeof stamp.toDate === "function") date = stamp.toDate();
    else if (typeof stamp.seconds === "number")
      date = new Date(stamp.seconds * 1000);
    else if (typeof jsonSeconds === "number")
      date = new Date(jsonSeconds * 1000);
    else return null;
  } else {
    date = new Date(String(value));
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Oct 10, 9:42 AM"; the year only when it isn't this year's. */
export function formatAttributionTime(
  value: unknown,
  { withTime = true, now = new Date() }: { withTime?: boolean; now?: Date } = {}
): string {
  const date = parseTimestamp(value);
  if (!date) return "";
  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" })
  });
  if (!withTime) return day;
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${day}, ${time}`;
}

function initialsOf(name: string): string {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

type Actor = { name: string; imageUrl: string | null };

/**
 * The person under a prefix (`filled_by`, `edited_by`), from what the entry
 * denormalized, then the resolver. A bare uid is not a name, so an actor known
 * only by uid counts as no actor.
 */
function actorOf(
  provenance: CasePrepUscisFieldProvenance,
  prefix: "filled_by" | "edited_by",
  resolveUser: ResolveFieldSourceUser | undefined,
  { ignoreStoredName = false }: { ignoreStoredName?: boolean } = {}
): Actor | null {
  const field = (suffix: string): string =>
    String(
      (provenance as Record<string, unknown>)[`${prefix}_${suffix}`] || ""
    ).trim();
  const uid = field("user_id");
  const resolved = uid ? resolveUser?.(uid) ?? null : null;
  const fullName = `${field("first_name")} ${field("last_name")}`.trim();
  const name = (
    (ignoreStoredName ? fullName : field("display_name") || fullName) ||
    String(resolved?.displayName || "").trim() ||
    (ignoreStoredName ? "" : field("email")) ||
    String(resolved?.email || "").trim()
  ).trim();
  if (!name) return null;
  const imageUrl =
    (ignoreStoredName ? "" : field("avatar_url")) ||
    String(resolved?.avatarUrl || "").trim();
  return { name, imageUrl: imageUrl || null };
}

function personAvatar(actor: Actor): AttributionAvatar {
  return {
    kind: "person",
    name: actor.name,
    initials: initialsOf(actor.name),
    imageUrl: actor.imageUrl
  };
}

/** Replaces `provenanceActorLabel`, adding the time and the agent and no-actor forms. */
export function fieldSourceAttribution(
  provenance: CasePrepUscisFieldProvenance | null | undefined,
  {
    resolveUser,
    now
  }: { resolveUser?: ResolveFieldSourceUser; now?: Date } = {}
): FieldSourceAttribution | null {
  if (!provenance) return null;

  if (provenance.kind === "manual_edit") {
    const actor = actorOf(provenance, "edited_by", resolveUser);
    const at = formatAttributionTime(provenance.edited_at, {
      withTime: Boolean(actor),
      now
    });
    return actor
      ? { parts: [actor.name, "Edited", at], avatar: personAvatar(actor) }
      : { parts: ["Edited", at], avatar: null };
  }

  const at = formatAttributionTime(provenance.completed_at, { now });

  if (provenance.kind === "filing_rule") {
    return { parts: ["Set by Filing Rules", at], avatar: null };
  }

  if (provenance.filled_by_agent) {
    // The agent run stores an old title ("Case agent") as its display name;
    // the person it acted for is only its uid.
    const principal = actorOf(provenance, "filled_by", resolveUser, {
      ignoreStoredName: true
    });
    return {
      parts: [principal ? `Casey for ${principal.name}` : "Casey", at],
      avatar: { kind: "agent" }
    };
  }

  const actor = actorOf(provenance, "filled_by", resolveUser);
  if (!actor) {
    return {
      parts: [
        "Autofilled",
        formatAttributionTime(provenance.completed_at, { withTime: false, now })
      ],
      avatar: null
    };
  }
  return { parts: [actor.name, "Autofilled", at], avatar: personAvatar(actor) };
}
